import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  COPILOT_CORNER_OPTIONS,
  COPILOT_SIZE_BOUNDS,
  COPILOT_SIZE_PRESETS,
  CopilotCorner,
  CopilotSettings as CopilotSettingsValue,
  getCopilotMaxHeight,
  getCopilotMaxWidth,
} from '@/features/copilot/constants'

interface CopilotSettingsProps {
  open: boolean
  settings: CopilotSettingsValue
  onChange: (patch: Partial<CopilotSettingsValue>) => void
  onReset: () => void
  onClose: () => void
}

const clampOffset = (value: number): number => {
  if (Number.isNaN(value)) return 0
  return Math.min(Math.max(value, 0), 400)
}

const clampWidth = (value: number, offsetX: number): number => {
  if (Number.isNaN(value)) return COPILOT_SIZE_BOUNDS.minWidth
  return Math.min(
    Math.max(value, COPILOT_SIZE_BOUNDS.minWidth),
    getCopilotMaxWidth(offsetX),
  )
}

const clampHeight = (value: number, offsetY: number): number => {
  if (Number.isNaN(value)) return COPILOT_SIZE_BOUNDS.minHeight
  return Math.min(
    Math.max(value, COPILOT_SIZE_BOUNDS.minHeight),
    getCopilotMaxHeight(offsetY),
  )
}

const CopilotSettings = ({
  open,
  settings,
  onChange,
  onReset,
  onClose,
}: CopilotSettingsProps) => {
  const activePresetLabel = COPILOT_SIZE_PRESETS.find(
    (p) => p.width === settings.width && p.height === settings.height,
  )?.label

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      sx={{ zIndex: 1500 }}
    >
      <DialogTitle sx={{ color: 'text.main' }}>Widget settings</DialogTitle>
      <DialogContent
        sx={{
          '& .MuiInputLabel-root': { color: 'grey.800' },
          '& .MuiInputBase-input': { color: 'text.main' },
        }}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="Corner"
            value={settings.corner}
            onChange={(e) =>
              onChange({ corner: e.target.value as CopilotCorner })
            }
            fullWidth
            size="small"
          >
            {COPILOT_CORNER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Offset X (px)"
              type="number"
              value={settings.offsetX}
              onChange={(e) =>
                onChange({ offsetX: clampOffset(Number(e.target.value)) })
              }
              size="small"
              fullWidth
              inputProps={{ min: 0, max: 400 }}
            />
            <TextField
              label="Offset Y (px)"
              type="number"
              value={settings.offsetY}
              onChange={(e) =>
                onChange({ offsetY: clampOffset(Number(e.target.value)) })
              }
              size="small"
              fullWidth
              inputProps={{ min: 0, max: 400 }}
            />
          </Box>

          <Box>
            <Typography
              variant="caption"
              sx={{ color: 'grey.600', display: 'block', mb: 0.5 }}
            >
              Panel size preset
            </Typography>
            <ButtonGroup size="small" fullWidth>
              {COPILOT_SIZE_PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  variant={
                    activePresetLabel === preset.label ? 'contained' : 'outlined'
                  }
                  onClick={() =>
                    onChange({ width: preset.width, height: preset.height })
                  }
                >
                  {preset.label}
                </Button>
              ))}
            </ButtonGroup>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Width (px)"
              type="number"
              value={settings.width}
              onChange={(e) =>
                onChange({
                  width: clampWidth(Number(e.target.value), settings.offsetX),
                })
              }
              size="small"
              fullWidth
              inputProps={{
                min: COPILOT_SIZE_BOUNDS.minWidth,
                max: getCopilotMaxWidth(settings.offsetX),
              }}
            />
            <TextField
              label="Height (px)"
              type="number"
              value={settings.height}
              onChange={(e) =>
                onChange({
                  height: clampHeight(
                    Number(e.target.value),
                    settings.offsetY,
                  ),
                })
              }
              size="small"
              fullWidth
              inputProps={{
                min: COPILOT_SIZE_BOUNDS.minHeight,
                max: getCopilotMaxHeight(settings.offsetY),
              }}
            />
          </Box>

          <Typography variant="caption" sx={{ color: 'grey.600' }}>
            Settings saved in your browser. You can also drag the corner of the
            chat panel to resize it.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onReset} size="small">
          Reset
        </Button>
        <Button onClick={onClose} size="small" variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CopilotSettings
